"use client"

import Image from "next/image"
import { Search, X } from "lucide-react"
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react"

import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"

export type BlogArchiveEntry = {
  slug: string
  title: string
  author: string
  category: string
  description?: string
  publishedAt?: string
  tags?: string[]
  coverImage?: {
    src: string
    alt: string
  }
}

type SortOrder = "archive" | "newest" | "oldest" | "title"

type TagOption = {
  key: string
  label: string
  count: number
}

const BLOG_FILTER_TAGS = [
  "addiction",
  "anxiety treatment",
  "best Kalamazoo therapist",
  "best therapist Kalamazoo",
  "best therapist near me",
  "Buddhist therapist",
  "Cognitive Behavioral Therapy",
  "Cognitive Behavioral Therapy Kalamazoo",
  "counseling",
  "counseling for women",
  "counseling Kalamazoo",
  "depression",
  "eating disorder",
  "family therapist Kalamazoo",
  "family therapy",
  "family therapy Kalamazoo",
  "food addiction",
  "food addiction counseling",
  "food addiction recovery",
  "holistic",
  "holistic healing",
  "holistic mental health",
  "holistic therapist",
  "holistic therapist Kalamazoo",
  "holistic therapy",
  "hormone therapy",
  "Kalamazoo",
  "Kalamazoo counseling",
  "Kalamazoo hormones",
  "Kalamazoo therapist",
  "Kalamazoo therapy",
  "mental health",
  "mental health Kalamazoo",
  "mindfulness",
  "Mindfulness-Based Stress Reduction",
  "mindful therapist",
  "nutrition and mental health",
  "self-care",
  "Self-love",
  "sugar addiction",
  "sugar addiction counseling",
  "sugar addiction recovery",
  "therapist near me",
  "therapy",
  "treatment for anxiety",
] as const

function formatBlogDate(value?: string) {
  if (!value) return ""
  const date = new Date(`${value}T00:00:00`)
  if (Number.isNaN(date.getTime())) return value

  return new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(date)
}

function normalizeValue(value: string) {
  return value.trim().toLocaleLowerCase()
}

function getEntrySearchText(entry: BlogArchiveEntry) {
  return [entry.title, entry.author, entry.category, entry.description, ...(entry.tags ?? [])]
    .filter(Boolean)
    .join(" ")
    .toLocaleLowerCase()
}

function compareByDate(left: BlogArchiveEntry, right: BlogArchiveEntry) {
  return (right.publishedAt ?? "").localeCompare(left.publishedAt ?? "")
}

export function BlogArchiveList({ entries, sidebar }: { entries: BlogArchiveEntry[]; sidebar?: ReactNode }) {
  const [query, setQuery] = useState("")
  const [selectedTag, setSelectedTag] = useState("")
  const [sortOrder, setSortOrder] = useState<SortOrder>("archive")
  const tagListRef = useRef<HTMLDivElement>(null)
  const tagButtonRefs = useRef<Record<string, HTMLButtonElement | null>>({})

  const tagOptions = useMemo<TagOption[]>(() => {
    const counts = new Map<string, number>()

    entries.forEach((entry) => {
      entry.tags?.forEach((tag) => {
        const key = normalizeValue(tag)
        if (!key) return

        counts.set(key, (counts.get(key) ?? 0) + 1)
      })
    })

    return BLOG_FILTER_TAGS.map((label) => {
      const key = normalizeValue(label)
      return { key, label, count: counts.get(key) ?? 0 }
    })
  }, [entries])

  const filteredEntries = useMemo(() => {
    const normalizedQuery = normalizeValue(query)
    const matchingEntries = entries.filter((entry) => {
      const matchesQuery = !normalizedQuery || getEntrySearchText(entry).includes(normalizedQuery)
      const matchesTag = !selectedTag || entry.tags?.some((tag) => normalizeValue(tag) === selectedTag)
      return matchesQuery && matchesTag
    })

    if (sortOrder === "newest") return [...matchingEntries].sort(compareByDate)
    if (sortOrder === "oldest") return [...matchingEntries].sort((left, right) => compareByDate(right, left))
    if (sortOrder === "title") return [...matchingEntries].sort((left, right) => left.title.localeCompare(right.title))
    return matchingEntries
  }, [entries, query, selectedTag, sortOrder])

  useEffect(() => {
    const tagList = tagListRef.current
    if (!tagList) return

    if (!selectedTag) {
      tagList.scrollTo({ left: 0, behavior: "smooth" })
      return
    }

    const activeTag = tagButtonRefs.current[selectedTag]
    if (!activeTag) return

    tagList.scrollTo({ left: Math.max(0, activeTag.offsetLeft - 4), behavior: "smooth" })
  }, [selectedTag])

  const clearFilters = () => {
    setQuery("")
    setSelectedTag("")
    setSortOrder("archive")
  }

  return (
    <section className="source-blog-section" aria-label="Blog archive">
      <div className="source-blog-controls" aria-label="Blog archive filters">
        <div className="source-blog-filter-row">
          <label className="source-blog-sort-control" htmlFor="blog-sort">
            <span>Sort</span>
            <Select id="blog-sort" value={sortOrder} onChange={(event) => setSortOrder(event.target.value as SortOrder)}>
              <option value="archive">Archive order</option>
              <option value="newest">Newest first</option>
              <option value="oldest">Oldest first</option>
              <option value="title">Title A–Z</option>
            </Select>
          </label>

          {tagOptions.length ? (
            <div className="source-blog-tags" aria-label="Filter by blog tag">
              <div className="source-blog-tag-list" ref={tagListRef} tabIndex={0} aria-label="Blog tags">
                <button
                  type="button"
                  className={`source-blog-tag${!selectedTag ? " is-active" : ""}`}
                  aria-pressed={!selectedTag}
                  onClick={() => setSelectedTag("")}
                >
                  All tags
                </button>
                {tagOptions.map((tag) => (
                  <button
                    key={tag.key}
                    ref={(element) => {
                      tagButtonRefs.current[tag.key] = element
                    }}
                    type="button"
                    className={`source-blog-tag${selectedTag === tag.key ? " is-active" : ""}`}
                    aria-label={selectedTag === tag.key ? `Remove ${tag.label} filter` : `Filter by ${tag.label}`}
                    aria-pressed={selectedTag === tag.key}
                    onClick={() => setSelectedTag((current) => current === tag.key ? "" : tag.key)}
                  >
                    <span>{tag.label}</span>
                    {selectedTag === tag.key ? <X aria-hidden="true" size={13} strokeWidth={2.2} /> : null}
                  </button>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <div className="source-blog-layout">
        <div className="source-blog-main">
          <div className="source-blog-search-row">
            <label className="source-blog-search-label" htmlFor="blog-search">
              <span className="source-blog-search-title">Search</span>
              <span className="source-blog-search-wrap">
                <Input
                  id="blog-search"
                  type="search"
                  aria-label="Search blog posts"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search by title, topic, or tag"
                  className="source-blog-search-input"
                />
                {query ? (
                  <button
                    type="button"
                    className="source-blog-search-action"
                    aria-label="Clear blog search"
                    onClick={() => setQuery("")}
                  >
                    <X aria-hidden="true" size={17} strokeWidth={2} />
                  </button>
                ) : (
                  <Search aria-hidden="true" className="source-blog-search-icon" size={17} strokeWidth={1.8} />
                )}
              </span>
            </label>
          </div>

          {filteredEntries.length ? (
            <div className="source-blog-collection" aria-label="Blog posts">
              {filteredEntries.map((entry) => {
                const image = entry.coverImage
                const resolvedHref = `/blog/${entry.slug}/`

                return (
                  <article key={entry.slug} className="source-blog-card">
                    {image ? (
                      <div className="source-blog-card-media">
                        <Image
                          src={image.src}
                          alt={image.alt || entry.title}
                          fill
                          sizes="(min-width: 1280px) 28vw, (min-width: 720px) 42vw, 100vw"
                          className="source-blog-card-image"
                        />
                      </div>
                    ) : null}
                    <div className="source-blog-card-content">
                      <div className="source-blog-card-topline">
                        <time dateTime={entry.publishedAt}>{formatBlogDate(entry.publishedAt)}</time>
                        {entry.category ? <span>{entry.category}</span> : null}
                        {entry.tags?.[0] ? <span>{entry.tags[0]}</span> : null}
                      </div>
                      <h3>{entry.title}</h3>
                      <p className="source-blog-card-byline">By: {entry.author}</p>
                      {entry.description ? <p className="source-blog-description">{entry.description}</p> : null}
                      <div className="source-blog-card-actions">
                        <a className="source-blog-card-link" href={resolvedHref}>
                          Read More
                          <span aria-hidden="true">↗</span>
                        </a>
                      </div>
                    </div>
                  </article>
                )
              })}
            </div>
          ) : (
            <div className="source-blog-empty" role="status">
              <strong>No articles match those filters.</strong>
              <button type="button" className="source-blog-reset" onClick={clearFilters}>Clear filters</button>
            </div>
          )}
        </div>
        {sidebar}
      </div>
    </section>
  )
}
